import type { GeneralOutcomeBatchPreview } from './contracts'

export type TimelineBatchKind = 'bye' | 'timeline'

type RecoveryBase = {
  version: 1
  ownerId: string
  batchKind: TimelineBatchKind
  createdAt: string
}

type UndoRecoveryBase = RecoveryBase & {
  batchId: string
  changed: number
  undoMutationId: string
}

export type TimelineBatchRecovery =
  | RecoveryBase & {
    phase: 'apply-pending'
    preview: GeneralOutcomeBatchPreview
    clientBatchId: string
  }
  | UndoRecoveryBase & { phase: 'reload-after-apply' }
  | UndoRecoveryBase & { phase: 'undo-available' }
  | UndoRecoveryBase & { phase: 'undo-pending' }
  | RecoveryBase & {
    phase: 'reload-after-undo'
    batchId: string
    changed: number
  }

const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const sha256Pattern = /^[0-9a-f]{64}$/

export function timelineBatchRecoveryKey(ownerId: string): string {
  if (!ownerId || ownerId !== ownerId.trim()) throw new Error('Timeline batch recovery requires an owner identity.')
  return `${ownerId}:outcome-batch`
}

export function encodeTimelineBatchRecovery(recovery: TimelineBatchRecovery): string {
  return JSON.stringify(recovery)
}

function isBase(value: Record<string, unknown>, ownerId: string): boolean {
  return value.version === 1 && value.ownerId === ownerId &&
    (value.batchKind === 'bye' || value.batchKind === 'timeline') &&
    typeof value.createdAt === 'string' && Number.isFinite(Date.parse(value.createdAt))
}

function isPreview(value: unknown): value is GeneralOutcomeBatchPreview {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const preview = value as Record<string, unknown>
  return Number.isSafeInteger(preview.expectedStructureVersion) && Number.isSafeInteger(preview.expectedScoreRevision) &&
    Boolean(preview.input && typeof preview.input === 'object' && !Array.isArray(preview.input)) &&
    Array.isArray(preview.cells) && typeof preview.previewFingerprint === 'string' && sha256Pattern.test(preview.previewFingerprint) &&
    typeof preview.confirmationText === 'string' && preview.confirmationText.length > 0
}

export function decodeTimelineBatchRecovery(raw: string | null, ownerId: string): TimelineBatchRecovery | null {
  if (!raw) return null
  try {
    const value = JSON.parse(raw) as unknown
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null
    const recovery = value as Record<string, unknown>
    if (!isBase(recovery, ownerId)) return null
    if (recovery.phase === 'apply-pending') {
      return isPreview(recovery.preview) && typeof recovery.clientBatchId === 'string' && uuidPattern.test(recovery.clientBatchId)
        ? value as TimelineBatchRecovery
        : null
    }
    if (recovery.phase === 'reload-after-apply' || recovery.phase === 'undo-available' || recovery.phase === 'undo-pending') {
      return typeof recovery.batchId === 'string' && uuidPattern.test(recovery.batchId) &&
        Number.isSafeInteger(recovery.changed) && Number(recovery.changed) >= 0 &&
        typeof recovery.undoMutationId === 'string' && uuidPattern.test(recovery.undoMutationId)
        ? value as TimelineBatchRecovery
        : null
    }
    if (recovery.phase === 'reload-after-undo') {
      return typeof recovery.batchId === 'string' && uuidPattern.test(recovery.batchId) &&
        Number.isSafeInteger(recovery.changed) && Number(recovery.changed) >= 0
        ? value as TimelineBatchRecovery
        : null
    }
    return null
  } catch {
    return null
  }
}
